/**
 * Product (stock + price) verification shared by Cumi and Magento checkouts.
 *
 * Both integrations run the same flow:
 *   1. Group cart items per retailer (single-brand or marketplace).
 *   2. Verify each group against the retailer's integration API.
 *   3. Report stock issues, numeric stock levels (Cumi only), price issues,
 *      and retailer IDs whose verification call failed.
 *
 * The only intentional difference: Cumi returns a numeric stock quantity so it
 * can also detect "requested quantity exceeds available stock". Magento only
 * returns an inStock boolean, so it can only report out-of-stock items.
 */
import type { CartItem } from "@/types";
import {
  verifyCumiSkus,
  type IntegrationConfigResponse,
  type MarketplaceRetailer,
} from "@/lib/supabase-api";
import { verifyMagentoProducts } from "@/lib/magento-helper";

export interface ProductVerificationGroup {
  retailerId: string;
  integration: "cumi" | "magento";
  items: CartItem[];
}

export interface StockIssue {
  item: CartItem;
  stock: number;
  type: "out_of_stock" | "quantity_exceeded";
}

export interface StockLevel {
  item: CartItem;
  stock: number;
}

export interface PriceIssue {
  item: CartItem;
  price: number;
}

export interface ProductVerificationResult {
  issues: StockIssue[];
  stockLevels: StockLevel[];
  priceIssues: PriceIssue[];
  failedRetailerIds: string[];
}

interface BuildProductVerificationGroupsInput {
  isMarketplace: boolean;
  cartItemsByBrand: [string, CartItem[]][];
  marketplaceRetailers: MarketplaceRetailer[];
  apiConfig: IntegrationConfigResponse | null;
  retailerId: string | null;
}

export function buildProductVerificationGroups({
  isMarketplace,
  cartItemsByBrand,
  marketplaceRetailers,
  apiConfig,
  retailerId,
}: BuildProductVerificationGroupsInput): ProductVerificationGroup[] {
  if (isMarketplace) {
    return marketplaceRetailers
      .map((retailer) => ({
        retailerId: retailer.retailer_id,
        integration: retailer.integration_type?.toLowerCase() ?? "",
      }))
      .filter(
        (retailer) =>
          retailer.integration === "cumi" || retailer.integration === "magento",
      )
      .map((retailer) => ({
        retailerId: retailer.retailerId,
        integration: retailer.integration as "cumi" | "magento",
        items:
          cartItemsByBrand.find(
            ([brandId]) => brandId === retailer.retailerId,
          )?.[1] ?? [],
      }))
      .filter((group) => group.items.length > 0);
  }

  const type = apiConfig?.type?.toLowerCase();
  if (!retailerId || (type !== "cumi" && type !== "magento")) {
    return [];
  }

  return [
    {
      retailerId,
      integration: type as "cumi" | "magento",
      items: cartItemsByBrand.flatMap(([, items]) => items),
    },
  ];
}

/** Cumi: numeric stock + price per SKU, with quantity-aware issue detection. */
async function verifyCumiGroup(
  group: ProductVerificationGroup,
  signal?: AbortSignal,
  sessionToken?: string,
): Promise<Omit<ProductVerificationResult, "failedRetailerIds">> {
  const response = await verifyCumiSkus(
    {
      retailerId: group.retailerId,
      products: group.items.map((item) => ({
        productId: item.product_id,
        sku: item.sku,
      })),
    },
    signal,
    sessionToken,
  );
  const verifiedBySku = new Map(response.map((item) => [item.sku, item]));
  const issues: StockIssue[] = [];
  const stockLevels = group.items.map((item) => ({
    item,
    stock: Math.max(
      0,
      Math.floor(verifiedBySku.get(item.sku)?.stock ?? 0),
    ),
  }));
  const priceIssues = group.items.flatMap((item) => {
    const verified = verifiedBySku.get(item.sku);
    if (!verified) return [];
    const price = Math.round(verified.price);
    return item.unit_price_cents === price ? [] : [{ item, price }];
  });

  for (const { item, stock } of stockLevels) {
    if (stock === 0) {
      issues.push({ item, stock: 0, type: "out_of_stock" });
    } else if (item.quantity > stock) {
      issues.push({ item, stock, type: "quantity_exceeded" });
    }
  }

  return { issues, stockLevels, priceIssues };
}

/** Magento: stock quantity per SKU, with quantity-aware issue detection. */
async function verifyMagentoGroup(
  group: ProductVerificationGroup,
  signal?: AbortSignal,
  sessionToken?: string,
): Promise<Omit<ProductVerificationResult, "failedRetailerIds">> {
  // Cart rows keep the variant SKU in `sku`; `selected_variant` is the title.
  const skus = [...new Set(group.items.map((item) => item.sku))];
  const response = await verifyMagentoProducts(
    group.retailerId,
    skus,
    signal,
    sessionToken,
  );
  const stockBySku = new Map(response.map((result) => [result.sku, result.stock]));

  const issues: StockIssue[] = [];
  const stockLevels: StockLevel[] = [];
  const priceIssues: PriceIssue[] = [];

  for (const item of group.items) {
    const stock = stockBySku.get(item.sku);
    if (stock === undefined) continue;
    stockLevels.push({ item, stock });
    if (stock === 0) {
      issues.push({ item, stock: 0, type: "out_of_stock" });
    } else if (item.quantity > stock) {
      issues.push({ item, stock, type: "quantity_exceeded" });
    }
  }

  return { issues, stockLevels, priceIssues };
}

export async function verifyProductGroups(
  groups: ProductVerificationGroup[],
  signal?: AbortSignal,
  sessionToken?: string,
): Promise<ProductVerificationResult> {
  const results = await Promise.all(
    groups.map(async (group) => {
      try {
        const verify =
          group.integration === "cumi" ? verifyCumiGroup : verifyMagentoGroup;
        return {
          retailerId: group.retailerId,
          ...(await verify(group, signal, sessionToken)),
          failed: false,
        };
      } catch (error) {
        if (signal?.aborted) throw error;
        return {
          retailerId: group.retailerId,
          issues: [] as StockIssue[],
          stockLevels: [] as StockLevel[],
          priceIssues: [] as PriceIssue[],
          failed: true,
        };
      }
    }),
  );

  return {
    issues: results.flatMap((result) => result.issues),
    stockLevels: results.flatMap((result) => result.stockLevels),
    priceIssues: results.flatMap((result) => result.priceIssues),
    failedRetailerIds: results
      .filter((result) => result.failed)
      .map((result) => result.retailerId),
  };
}
